import { msg } from "@lingui/core/macro";
import {
  buildReceiptLines,
  encodeReceiptEscPos,
  orderToReceiptData,
  renderReceiptText,
  shortOrderId,
} from "@comandero/shared";
import type { Order, OrderLine, Payment } from "@comandero/powersync";
import { createMemo, Show } from "solid-js";
import { Button } from "@/components/ui/button";
import { Dialog } from "@/components/ui/dialog";
import { useI18n } from "@/providers/i18n-provider";

interface ReceiptDialogProps {
  open: () => boolean;
  onOpenChange: (open: boolean) => void;
  order: () => Order | null;
  lines: () => OrderLine[];
  payments: () => Payment[];
  restaurantName: () => string;
  branchName?: () => string | null;
  tableName?: () => string | null;
  employeeName?: () => string | null;
}

/**
 * Printable order ticket: monospaced preview (what would come out of the
 * thermal printer, 42 columns) + download of the ESC/POS payload (`.bin`) ready
 * to send to an Epson-compatible printer (raw USB / network 9100 / RawBT).
 * Generation is 100% local and pure (`@comandero/shared/print`); sending to the
 * physical device is a manual step outside the browser.
 */
export function ReceiptDialog(props: ReceiptDialogProps) {
  const { t } = useI18n();

  const receiptLines = createMemo(() => {
    const order = props.order();
    if (!order) {
      return null;
    }
    const data = orderToReceiptData(order, props.lines(), props.payments(), {
      restaurantName: props.restaurantName(),
      branchName: props.branchName?.() ?? null,
      tableName: props.tableName?.() ?? null,
      employeeName: props.employeeName?.() ?? null,
      footer: t(msg`Thank you for your visit!`),
      labels: {
        order: t(msg`Order`),
        table: t(msg`Table`),
        server: t(msg`Sold by`),
        subtotal: t(msg`Subtotal`),
        discount: t(msg`Discount`),
        tax: t(msg`Tax`),
        total: t(msg`Total`).toUpperCase(),
        tendered: t(msg`Tendered`),
        change: t(msg`Change`),
        tip: t(msg`Tip`),
      },
    });
    return buildReceiptLines(data);
  });

  const previewText = createMemo(() => {
    const lines = receiptLines();
    return lines ? renderReceiptText(lines) : "";
  });

  function downloadPayload() {
    const lines = receiptLines();
    const order = props.order();
    if (!lines || !order) {
      return;
    }
    const payload = encodeReceiptEscPos(lines);
    // Copy into a "clean" ArrayBuffer for the Blob (strict TS typing).
    const buf = new ArrayBuffer(payload.byteLength);
    new Uint8Array(buf).set(payload);
    const blob = new Blob([buf], { type: "application/octet-stream" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `ticket-${shortOrderId(order.id)}.bin`;
    a.click();
    URL.revokeObjectURL(url);
  }

  return (
    <Dialog open={props.open()} onOpenChange={props.onOpenChange} contentClass="max-w-md">
      <div class="space-y-4" data-testid="receipt-dialog">
        <div>
          <h2 class="text-lg font-semibold">{t(msg`Receipt`)}</h2>
          <p class="mt-1 text-sm text-muted-foreground">
            {t(
              msg`Preview of the printed ticket. Download the ESC/POS file to send it to a thermal printer.`,
            )}
          </p>
        </div>
        <Show when={previewText()}>
          <pre
            class="max-h-[50vh] overflow-auto rounded-md border border-border/40 bg-muted/30 p-3 font-mono text-xs leading-snug"
            data-testid="receipt-preview"
          >
            {previewText()}
          </pre>
        </Show>
        <div class="flex justify-end gap-2">
          <Button variant="ghost" size="sm" onClick={() => props.onOpenChange(false)}>
            {t(msg`Close`)}
          </Button>
          <Button size="sm" data-testid="receipt-download-btn" onClick={downloadPayload}>
            {t(msg`Download ESC/POS`)}
          </Button>
        </div>
      </div>
    </Dialog>
  );
}
